import React, { useState } from "react";
import PropTypes from "prop-types";
import { FaSpinner } from "react-icons/fa";
import SlideDrawer from "./SlideDrawer";
import UnderlineInput from "./UnderlineInput";
import IsoDateInput, { formatIsoDay } from "./IsoDateInput";
import Clickable from "./Clickable";
import { searchErpInvoices, SUPPORTED_CURRENCIES } from "./erpInvoiceApi";

const EMPTY_FILTERS = { invoice_account: "", purch_id: "", invoice_id: "", invoice_date_from: "", invoice_date_to: "" };
const TEXT_FILTERS = [
  { key: "invoice_account", label: "Vendor account", placeholder: "e.g. RM00766" },
  { key: "purch_id", label: "Purchase order", placeholder: "e.g. PO1188024" },
  { key: "invoice_id", label: "Invoice no.", placeholder: "e.g. 1C26TCC-1040" },
];
const money = (v) => Number(v || 0).toLocaleString("en-US");
const LABEL = "tw-block tw-mb-1 tw-text-[12px] tw-font-medium tw-text-geist-fg-2";
const BUTTON = "tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-text-[13px] tw-font-semibold";

const InvoiceLines = ({ lines }) => (
  <table className="tw-w-full tw-mt-2 tw-text-[12px] tw-border-collapse">
    <thead>
      <tr className="tw-text-geist-fg-3 tw-text-left">
        <th className="tw-py-1 tw-font-medium">Item</th><th className="tw-py-1 tw-font-medium tw-text-right">Qty</th>
        <th className="tw-py-1 tw-font-medium tw-text-right">Amount</th><th className="tw-py-1 tw-font-medium tw-text-right">VAT</th>
      </tr>
    </thead>
    <tbody>
      {lines.map((l) => (
        <tr key={l.line_number} className="tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
          <td className="tw-py-1 tw-pr-2">{l.name || l.item_id}</td>
          <td className="tw-py-1 tw-text-right tw-tabular-nums">{money(l.qty)} {l.unit}</td>
          <td className="tw-py-1 tw-text-right tw-tabular-nums">{money(l.amount)}</td>
          <td className="tw-py-1 tw-text-right tw-tabular-nums">{money(l.tax)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const InvoiceCard = ({ inv, added, onAdd }) => {
  const [open, setOpen] = useState(false);
  const unsupported = !SUPPORTED_CURRENCIES.includes(inv.currency);
  let action;
  if (inv.used_by) {
    action = <span className="tw-text-[12px] tw-text-geist-fg-3">Used on {inv.used_by.code}</span>;
  } else if (added) {
    action = <span className="tw-text-[12px] tw-font-semibold tw-text-geist-success">Added</span>;
  } else if (unsupported) {
    action = <span className="tw-text-[12px] tw-text-geist-warning">{inv.currency} not supported</span>;
  } else {
    action = <Clickable onClick={() => onAdd(inv)} className={`${BUTTON} tw-bg-geist-accent tw-text-white`}>
      <i className="bi bi-plus-lg" /> Add</Clickable>;
  }
  return (
    <li className="tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <div className="tw-text-sm tw-font-semibold tw-text-geist-fg">
            #{inv.inv_no} <span className="tw-font-normal tw-text-geist-fg-3">· {formatIsoDay(inv.inv_date)}</span>
          </div>
          <div className="tw-text-[12px] tw-text-geist-fg-2 tw-truncate">
            {inv.vendor_account} · {inv.vendor_name}{inv.purch_id ? ` · ${inv.purch_id}` : ""}
          </div>
          <div className="tw-text-[12px] tw-text-geist-fg-3">{inv.description}</div>
        </div>
        <div className="tw-flex tw-flex-col tw-items-end tw-gap-1 tw-flex-shrink-0">
          <div className="tw-text-sm tw-font-semibold tw-text-geist-accent tw-tabular-nums">{money(inv.total)} {inv.currency}</div>
          {action}
        </div>
      </div>
      {inv.lines?.length > 0 && (
        <Clickable onClick={() => setOpen((o) => !o)} className="tw-mt-1 tw-text-[12px] tw-text-geist-accent">
          {open ? "Hide" : "Show"} {inv.lines.length} line{inv.lines.length > 1 ? "s" : ""}
        </Clickable>
      )}
      {open && <InvoiceLines lines={inv.lines} />}
    </li>
  );
};

/**
 * "From ERP": posted vendor invoices in AX, found by vendor / PO / invoice no. / date.
 * Add turns one invoice into one Payment invoices line (onAdd decides; returns true
 * when the line is on the request). Props: { show, onClose, onAdd, addedKeys: Set, paymentRequestId }
 */
const ErpInvoiceDrawer = ({ show, onClose, onAdd, addedKeys, paymentRequestId }) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const hasFilter = Object.values(filters).some((v) => String(v).trim());

  const search = async (event) => {
    event?.preventDefault();
    if (!hasFilter || loading) return;
    setLoading(true);
    setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => String(v).trim()));
      setRows(await searchErpInvoices({ ...params, payment_request_id: paymentRequestId || undefined }));
    } catch (e) {
      setRows(null);
      setError(e?.response?.data?.error || "Could not read vendor invoices from ERP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlideDrawer show={show} onHide={onClose} title="Add invoice from PO ERP" width="560px" minWidth="360px" bodyClassName="tw-p-0">
      <form onSubmit={search} className="tw-p-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
        {TEXT_FILTERS.map((f) => (
          <label key={f.key} className={f.key === "invoice_account" ? "sm:tw-col-span-2" : ""}>
            <span className={LABEL}>{f.label}</span>
            <UnderlineInput value={filters[f.key]} placeholder={f.placeholder} onChange={(e) => set(f.key)(e.target.value.toUpperCase())} />
          </label>
        ))}
        <div><span className={LABEL}>Invoice date from</span>
          <IsoDateInput aria-label="Invoice date from" value={filters.invoice_date_from} onChange={set("invoice_date_from")} /></div>
        <div><span className={LABEL}>Invoice date to</span>
          <IsoDateInput aria-label="Invoice date to" value={filters.invoice_date_to} onChange={set("invoice_date_to")} /></div>
        <div className="sm:tw-col-span-2 tw-flex tw-items-center tw-justify-between tw-gap-3">
          <span className="tw-text-[12px] tw-text-geist-fg-3">Invoices already posted in AX: each becomes one line, and the request is paid with journal APP only.</span>
          <button type="submit" disabled={!hasFilter || loading}
            className={`${BUTTON} tw-border-0 tw-bg-geist-accent tw-text-white disabled:tw-opacity-50 tw-cursor-pointer`}>
            {loading ? <FaSpinner className="tw-animate-spin" size={11} /> : <i className="bi bi-search" />} Search
          </button>
        </div>
      </form>
      {error && <div role="alert" className="tw-p-4 tw-text-sm tw-text-geist-error">{error}</div>}
      {rows && rows.length === 0 && <div className="tw-p-6 tw-text-center tw-text-sm tw-text-geist-fg-3">No posted invoice matches.</div>}
      {rows && rows.length > 0 && (
        <ul className="tw-list-none tw-m-0 tw-p-0">
          {rows.map((inv) => <InvoiceCard key={inv.key} inv={inv} added={addedKeys.has(inv.document_id)} onAdd={onAdd} />)}
        </ul>
      )}
    </SlideDrawer>
  );
};

const LINE_SHAPE = PropTypes.shape({
  line_number: PropTypes.number, item_id: PropTypes.string, name: PropTypes.string, unit: PropTypes.string,
  qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
});
InvoiceLines.propTypes = { lines: PropTypes.arrayOf(LINE_SHAPE).isRequired };
InvoiceCard.propTypes = {
  inv: PropTypes.shape({
    inv_no: PropTypes.string, inv_date: PropTypes.string, vendor_account: PropTypes.string, vendor_name: PropTypes.string,
    purch_id: PropTypes.string, currency: PropTypes.string, description: PropTypes.string,
    total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), lines: PropTypes.arrayOf(LINE_SHAPE),
    used_by: PropTypes.shape({ code: PropTypes.string }),
  }).isRequired,
  added: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
};

ErpInvoiceDrawer.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  addedKeys: PropTypes.instanceOf(Set).isRequired,
  paymentRequestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ErpInvoiceDrawer;
